import _styled from "styled-components";
import React from "react";
import type MuxPlayerElement from "@mux/mux-player";
import { Icon, type IconComponent } from "~/features/ui/icon";
import Pause from "~/public/icons/pause.svg";
import Play from "~/public/icons/play.svg";
import { useMediaQuery } from "~/features/ui/use-media-query";
import { up } from "~/features/app/screens";
type Props = {
  videoRef?: React.RefObject<HTMLVideoElement | MuxPlayerElement | null>;
  size?: keyof typeof sizes;
  variant?: keyof typeof variants;
  icon?: IconComponent;
  [key: string]: unknown;
};
const sizes = {
  small: {
    "height": "4rem",
    "width": "4rem"
  },
  medium: {
    "height": "4.8rem",
    "width": "4.8rem",
    "@media (min-width: 1024px)": {
      "height": "5.6rem",
      "width": "5.6rem"
    }
  },
  large: {
    "height": "4rem",
    "width": "4rem",
    "@media (min-width: 1024px)": {
      "height": "7.2rem",
      "width": "7.2rem"
    }
  }
} as const;
const variants = {
  primary: {
    "backgroundColor": "rgba(0, 7, 4, 0.4)",
    "--tw-text-opacity": "1",
    "color": "rgb(255 255 255 / var(--tw-text-opacity))",
    "@media (hover: hover)": {
      "&:hover": {
        "backgroundColor": "rgba(0, 7, 4, 0.6)"
      }
    }
  },
  secondary: {
    "--tw-bg-opacity": "1",
    "backgroundColor": "rgb(255 255 255 / var(--tw-bg-opacity))",
    "--tw-text-opacity": "1",
    "color": "rgb(0 7 4 / var(--tw-text-opacity))"
  },
  tertiary: {
    "borderRadius": "2px",
    "backgroundColor": "rgb(255 255 255 / 0.1)",
    "--tw-text-opacity": "1",
    "color": "rgb(255 255 255 / var(--tw-text-opacity))",
    "--tw-backdrop-blur": "blur(24px)",
    "backdropFilter": "var(--tw-backdrop-blur) var(--tw-backdrop-brightness) var(--tw-backdrop-contrast) var(--tw-backdrop-grayscale) var(--tw-backdrop-hue-rotate) var(--tw-backdrop-invert) var(--tw-backdrop-opacity) var(--tw-backdrop-saturate) var(--tw-backdrop-sepia)"
  }
} as const;
const buttonStyle = {
  "position": "relative",
  "display": "flex",
  "flexShrink": "0",
  "alignItems": "center",
  "justifyContent": "center",
  "transitionProperty": "color, background-color, border-color, text-decoration-color, fill, stroke",
  "transitionDuration": "250ms",
  "transitionTimingFunction": "ease-in-out",
  "&::after": {
    "content": "var(--tw-content)",
    "position": "absolute",
    "bottom": "0",
    "left": "0",
    "height": "0.2rem",
    "width": "100%",
    "transformOrigin": "left",
    "--tw-bg-opacity": "1",
    "backgroundColor": "rgb(75 46 248 / var(--tw-bg-opacity))",
    "--tw-content": "\"\"",
    "transform": "scaleX(var(--progress))"
  }
};
export function VideoControls(props: Props & React.ComponentPropsWithRef<"button">) {
  const {
    size = "small",
    variant = "primary",
    videoRef,
    icon,
    onClick,
    ...rest
  } = props;
  const isDecorativeElement = rest.as != null && rest.as !== "button";
  const [isPlaying, setIsPlaying] = React.useState(false);
  const [progress, setProgress] = React.useState(0);
  const isLg = useMediaQuery(up("lg"));
  const toggle = (event: React.MouseEvent<HTMLButtonElement>) => {
    const video = videoRef?.current;
    if (video && typeof video.play === "function") {
      if (video.paused || video.ended) {
        video.play();
      } else {
        video.pause();
      }
    }
    if (onClick) {
      onClick(event);
    }
  };

  // The lazy-loaded <mux-player> attaches to videoRef.current after first
  // paint, but RefObjects don't trigger re-renders. Poll once via rAF until
  // the element is available, then mirror it into state so the
  // play/pause/ended/timeupdate listeners can subscribe.
  const [video, setVideo] = React.useState<HTMLVideoElement | MuxPlayerElement | null>(() => videoRef?.current ?? null);
  React.useEffect(() => {
    if (videoRef?.current) {
      setVideo(videoRef.current);
      return;
    }
    let frame = 0;
    const tick = () => {
      if (videoRef?.current) {
        setVideo(videoRef.current);
        return;
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [videoRef]);
  React.useEffect(() => {
    if (!video || typeof video.addEventListener !== "function") {
      return;
    }
    setIsPlaying(!video.paused);
    function onPlay() {
      setIsPlaying(true);
    }
    function onPause() {
      setIsPlaying(false);
    }
    function onEnded() {
      setIsPlaying(false);
    }
    function onTimeUpdate() {
      setProgress(video!.currentTime / video!.duration);
    }
    video.addEventListener("play", onPlay);
    video.addEventListener("pause", onPause);
    video.addEventListener("ended", onEnded);
    video.addEventListener("timeupdate", onTimeUpdate);
    return () => {
      video.removeEventListener("play", onPlay);
      video.removeEventListener("pause", onPause);
      video.removeEventListener("ended", onEnded);
      video.removeEventListener("timeupdate", onTimeUpdate);
    };
  }, [video]);
  return <_StyledButton style={{
    "--progress": `${progress}`
  } as React.CSSProperties} aria-label={isDecorativeElement ? undefined : isPlaying ? "Pause video" : "Play video"} aria-hidden={isDecorativeElement ? true : undefined} onClick={toggle} {...rest} $_css={[buttonStyle, sizes[size], variants[variant]]}>
      <Icon icon={icon ? icon : isPlaying ? Pause : Play} size={isLg ? "16" : "12"} />
    </_StyledButton>;
}
var _StyledButton = /*#__PURE__*/_styled("button").withConfig({
  displayName: "video-controls___StyledButton",
  componentId: "sc-1s0d75j-0"
})(["", ""], p => p.$_css);